/**
 * Библиотека для преобразования и форматирования размеров файлов и папок.
 */

export interface Space {
    original: number;
    space: number;
    units: string;
    value: string;
}

export const Spaces = {
    ONE_KB: 1024,
    ONE_MB: 1024 ** 2,
    ONE_GB: 1024 ** 3,
    ONE_TB: 1024 ** 4,
    ONE_PB: 1024 ** 5,
};

const bounds = [Spaces.ONE_KB, Spaces.ONE_MB, Spaces.ONE_GB, Spaces.ONE_TB, Spaces.ONE_PB];
const units = ['КБ', 'МБ', 'ГБ', 'ТБ', 'ПБ'];
const roundMethod = 'round';

const getDigitsCount = (value: number): number => {
    return String(value).replace('.', '').length;
};

const round = (value: number, precision: number): number => {
    return Math[roundMethod](value * precision) / precision;
};

const reducePrecision = (bytes: number, divisor: number, maxDigitsCount: number, precision: number): number => {
    const roundedValue = round(bytes / divisor, precision);

    if (getDigitsCount(roundedValue) > maxDigitsCount) {
        precision = precision / 10;

        if (precision >= 1) {
            return reducePrecision(bytes, divisor, maxDigitsCount, precision);
        }
    }

    return roundedValue;
};

const getDivisor = (bytes: number): number => {
    let i = bounds.length;

    while (i--) {
        const bound = bounds[i];

        if (bound < bytes) {
            return bound;
        }
    }

    return 1;
};

const getPrecision = (maxDigitsCount: number): number => {
    return Math.pow(10, maxDigitsCount - 1);
};

/**
 * Приводит количество байт к размеру не больше чем в N цифрах.
 * @param {number} bytes – целое число байт.
 * @param {number} maxDigitsCount – максимальное количество цифр.
 * @param {number} divisor - делитель?
 * @returns {Object}
 */
const toNDigits = (bytes: number, maxDigitsCount: number, divisor?: number): Space => {
    let currUnits = 'байт';
    let roundedSpace = bytes;

    if (getDigitsCount(bytes) > maxDigitsCount) {
        if (!divisor) {
            divisor = getDivisor(bytes);
        }

        roundedSpace = reducePrecision(bytes, divisor, maxDigitsCount, getPrecision(maxDigitsCount));

        if (getDigitsCount(roundedSpace) > maxDigitsCount) {
            return toNDigits(bytes, maxDigitsCount, divisor * 1024);
        }

        currUnits = units[bounds.indexOf(divisor)];
    }

    return {
        original: bytes,
        units: currUnits,
        space: roundedSpace,
        value: `${roundedSpace} ${currUnits}`,
    };
};

const bytesToNDigits = (bytes: number, maxDigitsCount: number): Space => {
    const space = toNDigits(bytes, maxDigitsCount);

    space.original = bytes;

    return space;
};

export { toNDigits, bytesToNDigits };
